


import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Grid, Paper, Table, TableContainer,
  TableHead, TableBody, IconButton, MenuItem, Autocomplete, TextField,
} from "@mui/material";
import { StyledTableCell, StyledTableRow } from "../../reusableComponent/Reusables";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import cylinderMasterService from "../../../services/masters/cylinderMaster.Service";
import productMasterService from "../../../services/masters/productMaster.Service";
import customerProductService from "../../../services/masters/customerProductMaster.Service.js";
import accountManagerSeervice from "../../../services/masters/accountManagerMaster.Service.js";
import customerMasterService from "../../../services/masters/customerMaster.Service.js";
import uomMasterService from "../../../services/masters/uomMaster.Service.js";
import customerPoService from "../../../services/customerPo.Service.js";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

// ── Styles ────────────────────────────────────────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px", color: "#718096", mb: 2,
  display: "flex", alignItems: "center", gap: "6px",
};

const pageHeaderSx = {
  display: "flex", alignItems: "center",
  justifyContent: "space-between", mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px", fontWeight: 700, color: "#000",
  display: "flex", alignItems: "center", gap: "10px",
};

const backBtnSx = {
  display: "flex", alignItems: "center", gap: "6px",
  fontSize: "13px", fontWeight: 600, color: "#2B6CB0",
  padding: "7px 14px", border: "1.5px solid #2B6CB0",
  borderRadius: "7px", background: "#fff", textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff", borderRadius: "14px",
  border: "1px solid #E2E8F0", overflow: "hidden", mb: 2,
};

const sectionHeaderSx = {
  background: "#1C3A5E", padding: "12px 22px",
  display: "flex", alignItems: "center", gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px", fontWeight: 600, color: "#fff", letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
  fontSize: "12px", fontWeight: 600, color: "#2063b6",
  mb: "4px", display: "flex", alignItems: "center", gap: "4px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa", height: "38px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F8FAFC" },
  },
  "& input": {
    color: "#1A202C", fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const readOnlyInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    background: "#F0F4F8",
  },
};

const datePickerSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa", height: "38px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
  },
  "& input": { fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" },
};

const formFooterSx = {
  padding: "18px 24px", background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex", alignItems: "center",
  justifyContent: "flex-end", gap: "10px",
};

const saveBtnSx = {
  display: "flex", alignItems: "center", gap: "7px",
  padding: "0 22px", height: "40px", background: "#4CAF50",
  color: "#fff", border: "none", fontSize: "14px", fontWeight: 600,
  textTransform: "none", borderRadius: "8px",
  "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
  transition: "all 0.15s ease",
};

const cancelBtnSx = {
  display: "flex", alignItems: "center", gap: "7px",
  padding: "0 22px", height: "40px", background: "#E53E3E",
  color: "#fff", border: "none", textTransform: "none",
  "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
  fontSize: "14px", fontWeight: 600, borderRadius: "8px",
};

const tableHeadCellSx = {
  background: "#1C3A5E", color: "#fff",
  fontSize: "12px", fontWeight: 600,
  fontFamily: "'Inter', Arial, sans-serif",
  whiteSpace: "nowrap",
};

const tableCellInputSx = {
  "& .MuiInput-root": {
    fontSize: "12px",
    "&:before": { borderBottom: "none" },
    "&:after": { borderBottom: "none" },
    "&:hover:not(.Mui-disabled):before": { borderBottom: "none" },
  },
  "& input": { fontSize: "12px", padding: "4px 6px" },
};

const tableSelectSx = {
  "& .MuiInput-root": {
    fontSize: "12px",
    "&:before": { borderBottom: "none" },
    "&:after": { borderBottom: "none" },
  },
  "& .MuiSelect-select": { fontSize: "12px", padding: "4px 6px" },
};

// ─────────────────────────────────────────────────────────────────────────────

const ScheduleEntryAdd = () => {
  const { handleSubmit } = useForm();
  const navigate = useNavigate();
  const urlLocation = useLocation();
  const editData = urlLocation?.state;
  const editid = editData?.customer_id || null;

  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;
  const unit_id = userInfo.depot_id || userInfo.plant_id;
  const short_code = userInfo.depot_short_code || userInfo.plant_short_code;

  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [salesofficerdata, setSalesofficerdata] = useState({ id: "", sales_officer_name: "" });
  const [isUpdateArray, setIsUpdateArray] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
    const [poFile, setPoFile] = useState(null);

  const handlePoFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setSuccessMessage("Error: Only PDF, JPG or PNG file allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setSuccessMessage("Error: File size must be under 5MB");
      return;
    }
    setPoFile(file);
  };
  const [isLoadingSalesOfficer, setIsLoadingSalesOfficer] = useState(false);

  const [data, setData] = useState({
    entry_no: editData?.entry_no || "",
    entry_date: editData ? dayjs(editData.entry_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
    customer_name: editData?.customer_name || "",
    sales_officer_name: editData?.sales_officer_name || "",
    delivery_address: editData?.delivery_address || "",
    po_no: editData?.po_no || "",
    po_date: editData ? dayjs(editData.po_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
    so_date: editData ? dayjs(editData.so_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
    fin_year: editData?.fin_year || "",
    sales_officer_id: editData?.sales_officer_id || "",
  });

  const [child_details, setchild_details] = useState(() => {
    if (editData) {
      try {
        const parsed = typeof editData.child_details === "string"
          ? JSON.parse(editData.child_details) : editData.child_details;
        return Array.isArray(parsed)
          ? parsed.map(item => ({ ...item, req_date: item.req_date ? dayjs(item.req_date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD") }))
          : [defaultRow()];
      } catch { return [defaultRow()]; }
    }
    return [defaultRow()];
  });

  function defaultRow() {
    return { prod_id: "", prod_description: "", prod_code: "", qty_kg: "", req_date: dayjs().format("YYYY-MM-DD"), unit_rate: "", transport_cost: "", uom: "m3", basic_amount: "", gst_percentage: "", gst_amount: "", total_amount: "" };
  }

  const [Product, setProduct] = useState([]);
  const [account_manager, setAccountManager] = useState([]);
  const [customer, setCustomer] = useState([]);
  const [uom, setUOM] = useState([]);
  const [schedule_entry, setScheduleEntry] = useState([]);

 useEffect(() => {
  async function fetchData() {
    try {
      const [ProductData, cylinderData, account_managerData, customerData, cpdData, uomData, schedule_entryData] = await Promise.all([
        productMasterService.getProductList({ page: 0, size: 999, searchKey: "" }),
        cylinderMasterService.getCylinderList({ page: 0, size: 999, searchKey: "" }),
        accountManagerSeervice.getAccountManagerList({ page: 0, size: 999, searchKey: "" }),
        customerMasterService.getCustomerList({ page: 0, size: 999, searchKey: "" }),
        customerProductService.getCpdList({ page: 0, size: 999, searchKey: "" }),
        uomMasterService.getUOMList({ page: 0, size: 999, searchKey: "" }),
        customerPoService.getScheduleEntryList({ page: 0, size: 5, searchKey: "" }),
      ]);
      setProduct(ProductData);
      setAccountManager(account_managerData);
      setCustomer(customerData);
      setUOM(uomData);
      setScheduleEntry(schedule_entryData);

      // ✅ Entry No Generation
      if (!editData) {
        const yr = dayjs();
        const finYear = yr.month() >= 3
          ? `${String(yr.year()).slice(-2)}-${String(yr.year() + 1).slice(-2)}`
          : `${String(yr.year() - 1).slice(-2)}-${String(yr.year()).slice(-2)}`;

        const sameYearEntries = schedule_entryData?.filter(d => d.entry_no?.includes(finYear)) ?? [];
        const lastEntryNo = sameYearEntries?.[0]?.entry_no ?? "";
        const parts = String(lastEntryNo).split("/");
        const lastSeq = parseInt(parts[parts.length - 1]) || 0;
        const nextSeq = lastSeq + 1;

      const formattedEntryNo = `PO/${finYear}/${String(nextSeq).padStart(4, "0")}`;
        console.log("Generated entry_no:", formattedEntryNo);

        setData(prev => ({
          ...prev,
          entry_no: formattedEntryNo,
          fin_year: finYear,
        }));
      }
    } catch (error) { console.error("Data fetching failed", error); }
  }
  fetchData();
}, []);

  useEffect(() => {
    if (!selectedCustomerId) { setSalesofficerdata({ sales_officer_id: "", sales_officer_name: "" }); return; }
    async function fetchSalesOfficer() {
      try {
        setIsLoadingSalesOfficer(true);
        const result = await customerPoService.getsalesofficerdata(selectedCustomerId);
        setSalesofficerdata(result || { sales_officer_id: "", sales_officer_name: "" });
        setData(prev => ({ ...prev, sales_officer_id: result?.sales_officer_id || "", sales_officer_name: result?.sales_officer_name || "" }));
      } catch { setSalesofficerdata({ sales_officer_id: "", sales_officer_name: "" }); }
      finally { setIsLoadingSalesOfficer(false); }
    }
    fetchSalesOfficer();
  }, [selectedCustomerId]);

  useEffect(() => {
    if (editData && customer?.length > 0) {
      const selected = customer.find(c => c.cust_name === editData.customer_name);
      if (selected) {
        setSelectedCustomer(selected);
        setData(prev => ({ ...prev, customer_name: selected.cust_name, customer_id: selected.cust_id }));
        setSelectedCustomerId(selected.cust_id);
      }
    }
  }, [editData, customer]);

  useEffect(() => {
    if (editData && account_manager?.length > 0) {
      const matchedOfficer = account_manager.find(so => String(so.id) === String(editData.sales_officer_id));
      setSalesofficerdata(matchedOfficer || { id: editData.sales_officer_id || "", sales_officer_name: editData.sales_officer_name || "" });
      setData(prev => ({ ...prev, sales_officer_id: editData.sales_officer_id || "", sales_officer_name: matchedOfficer?.sales_officer_name || editData.sales_officer_name || "" }));
    }
  }, [editData, account_manager]);

  const handleChange = ({ target }) => {
    const { name, value } = target;
    let updatedData = { ...data, [name]: ["entry_date", "po_date"].includes(name) ? (value ? dayjs(value).format("YYYY-MM-DD") : "") : value };
    if (name === "entry_date" && value) {
      const m = dayjs(value).month(), y = dayjs(value).year();
      updatedData.fin_year = m >= 3 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
    }
    setData(updatedData);
  };

  const handleChangeCustomer = (event) => {
    const selectedCustomerName = event.target.value;
    const sel = customer.find(c => c.cust_name === selectedCustomerName);
    if (sel) {
      const matchedOfficer = account_manager.find(so => String(so.id) === String(sel.sales_officer_id));
      setData({ ...data, customer_name: sel.cust_name, customer_id: sel.cust_id, sales_officer_id: matchedOfficer?.id || sel.sales_officer_id || "", sales_officer_name: matchedOfficer?.sales_officer_name || "", delivery_address: sel.cust_address || "" });
      setSalesofficerdata(matchedOfficer || { id: sel.sales_officer_id || "", sales_officer_name: "" });
      setSelectedCustomerId(sel.cust_id);
      setSelectedCustomer(sel);
      setchild_details(prev => prev.map(d => ({ ...d, prod_id: "", prod_description: "", prod_code: "", uom: "m3", unit_rate: "", transport_cost: "", qty_kg: "", req_date: d.req_date || dayjs().format("YYYY-MM-DD") })));
    } else {
      setData({ ...data, customer_name: "", customer_id: "", sales_officer_id: "", sales_officer_name: "", delivery_address: "" });
      setSalesofficerdata({ id: "", sales_officer_name: "" });
      setSelectedCustomerId(null);
      setSelectedCustomer(null);
    }
  };

  const handleChangeInput = (e, index) => {
    const { name, value } = e.target;
    setchild_details(prev => {
      const updated = [...prev];
      if (!updated[index]) updated[index] = {};
      if (name === "prod_code") {
        const isDuplicate = prev.some((item, idx) => idx !== index && item.prod_code === value);
        if (isDuplicate) { alert("Duplicate product code is not allowed"); return prev; }
        const sel = Product.find(item => item.prod_code === value);
        updated[index] = { ...updated[index], prod_code: value, prod_description: sel?.prod_description || "", prod_id: sel?.prod_id || "", uom: "m3" };
      } else if (name === "req_date") {
        updated[index] = { ...updated[index], [name]: dayjs(value).format("YYYY-MM-DD") };
      } else {
        updated[index] = { ...updated[index], [name]: value };
      }
      if (["qty_kg", "unit_rate", "gst_percentage"].includes(name)) {
        const qty = parseFloat(updated[index].qty_kg) || 0;
        const rate = parseFloat(updated[index].unit_rate) || 0;
        const gst = parseFloat(updated[index].gst_percentage) || 0;
        const basic = qty * rate;
        const gstAmt = (basic * gst) / 100;
        updated[index] = { ...updated[index], basic_amount: basic.toFixed(2), gst_amount: gstAmt.toFixed(2), total_amount: (basic + gstAmt).toFixed(2) };
      }
      return updated;
    });
  };

  const handleAddScheduleFields = () => {
    setchild_details(prev => [...prev, { ...defaultRow(), srno: prev.length + 1 }]);
  };

  const handleRemoveScheduledetails = (index) => {
    setchild_details(prev => { const v = [...prev]; v.splice(index, 1); return v; });
    setIsUpdateArray(p => !p);
  };

  const onSubmit = async () => {
    if (!data.sales_officer_id && !salesofficerdata?.id) { setSuccessMessage("Error: Sales officer ID is required"); return; }
    if (!data.entry_date || !data.po_date || child_details.some(item => !item.req_date)) { setSuccessMessage("Error: All date fields are required"); return; }
    const isEdit = !!editData;
    const se_Data = {
      ...data,
      entry_id: isEdit ? editData.entry_id : null,
      customer_id: data.customer_id || selectedCustomer?.cust_id || editid,
      cust_name: data.customer_name || selectedCustomer?.cust_name,
      sales_officer_id: data.sales_officer_id || salesofficerdata?.id || "",
      sales_officer_name: data.sales_officer_name || salesofficerdata?.sales_officer_name || "",
      entry_date: dayjs(data.entry_date).format("YYYY-MM-DD"),
      po_date: dayjs(data.po_date).format("YYYY-MM-DD"),
      so_date: data.so_date ? dayjs(data.so_date).format("YYYY-MM-DD") : "",
      child_details: isEdit
        ? child_details.map(item => ({ ...item, req_date: dayjs(item.req_date).format("YYYY-MM-DD") }))
        : JSON.stringify(child_details.map(item => ({ ...item, req_date: dayjs(item.req_date).format("YYYY-MM-DD") }))),
      changedUpdatedValue: "edit",
      modified_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      modified_by: isEdit ? userInfo.user_id : null,
      created_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      created_by: isEdit ? editData.created_by : userInfo.user_id,
      unit_id, short_code, ...(!isEdit && poFile && { upload_po: poFile }),
    };
    try {
      isEdit ? await customerPoService.editDeleteScheduleEntry(se_Data) : await customerPoService.addScheduleEntry(se_Data);
      setSuccessMessage(isEdit ? "Updated successfully" : "Added successfully");
      setTimeout(() => navigate(-1), 500);
    } catch (error) {
      setSuccessMessage("Error occurred: " + error.message);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageSx}>
        {/* Breadcrumb */}
        <Box sx={breadcrumbSx}>
          <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Customer PO</span>
        </Box>

        {/* Page Header */}
        <Box sx={pageHeaderSx}>
          <Box sx={pageTitleSx}>
            <DescriptionIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
            {editData ? "UPDATE" : "CREATE"} CUSTOMER PO
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {!editData && (
              <Typography sx={{ fontSize: "12px", color: "#718096", fontWeight: 500 }}>
                Last Entry No: <strong>{schedule_entry?.[0]?.lastScheduleEntryNo ?? 0}</strong>
              </Typography>
            )}
            <Button onClick={() => navigate(-1)} sx={backBtnSx}>
              <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
            </Button>
          </Box>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* ── Section 1: Customer & PO Details ── */}
         {/* ── Section 1: Customer & PO Details ── */}
<Box sx={formCardSx}>
  <Box sx={sectionHeaderSx}>
    <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />
    <Typography sx={sectionHeaderTitleSx}>Customer & PO Details</Typography>
  </Box>
  <Box sx={formBodySx}>
    <Grid container sx={{ border: "1px solid #E2E8F0", borderRadius: "10px", overflow: "hidden" }}>

      {/* ════ LEFT COLUMN ════ */}
      <Grid item xs={12} md={6} sx={{ p: 2.5, borderBottom: { xs: "2px dashed #CBD5E0", md: "none" } }}>
        <Grid container spacing={1.5}>

          {/* Customer Name */}
          <Grid item xs={12} sm={9}>
            <Typography sx={fieldLabelSx}>Customer Name <span style={{ color: "#E53E3E" }}>*</span></Typography>
            <Autocomplete
              disablePortal options={customer || []}
              getOptionLabel={opt => typeof opt === "string" ? opt : opt.cust_name || ""}
              value={customer.find(c => c.cust_name === data.customer_name) || null}
              onChange={(_, newVal) => handleChangeCustomer({ target: { name: "customer_name", value: newVal ? newVal.cust_name : "" } })}
              renderInput={params => <TextField {...params} size="small" fullWidth required placeholder="Select customer" sx={inputSx} />}
            />
          </Grid>

          {/* Sales Officer */}
          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Sales Officer</Typography>
            <TextField size="small" fullWidth value={salesofficerdata?.sales_officer_name || ""}
              sx={readOnlyInputSx} InputProps={{ readOnly: true }} placeholder="Auto-filled"
            />
          </Grid>

          {/* Delivery Address */}
          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>Delivery Address <span style={{ color: "#E53E3E" }}>*</span></Typography>
            <TextField size="small" fullWidth required placeholder="Delivery address"
              value={data.delivery_address}
              onChange={e => setData(prev => ({ ...prev, delivery_address: e.target.value }))}
              sx={inputSx}
            />
          </Grid>

          {/* PO No */}
          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>PO No</Typography>
            <TextField size="small" fullWidth name="po_no"
              value={data.po_no} onChange={handleChange}
              placeholder="Enter PO number" sx={inputSx}
            />
          </Grid>

          {/* PO Date */}
          <Grid item xs={12} sm={6}>
            <Typography sx={fieldLabelSx}>PO Date <span style={{ color: "#E53E3E" }}>*</span></Typography>
            <DatePicker format="DD/MM/YYYY"
              value={data.po_date ? dayjs(data.po_date) : null}
              onChange={v => handleChange({ target: { name: "po_date", value: v ? v.format("YYYY-MM-DD") : "" } })}
              sx={datePickerSx}
              slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
            />
          </Grid>

        </Grid>
      </Grid>

      {/* ════ RIGHT COLUMN — Entry No + Entry Date ════ */}
      <Grid item xs={12} md={6} sx={{ p: 2.5, background: "#FAFBFC", pl: 3.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>

          {/* Entry No */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Entry No</Typography>
            <TextField size="small" fullWidth name="entry_no"
              value={data.entry_no} sx={readOnlyInputSx}
              InputProps={{ readOnly: true }}
              placeholder="Auto-generated"
            />
          </Box>

          {/* Entry Date */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={fieldLabelSx}>Entry Date <span style={{ color: "#E53E3E" }}>*</span></Typography>
            <DatePicker format="DD/MM/YYYY"
              value={data.entry_date ? dayjs(data.entry_date) : null}
              onChange={v => handleChange({ target: { name: "entry_date", value: v ? v.format("YYYY-MM-DD") : "" } })}
              sx={datePickerSx}
              slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
            />
          </Box>

        </Box>
                {/* Upload PO */}
        {!editData && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={fieldLabelSx}>
              Upload PO <span style={{ color: "#718096", fontWeight: 400 }}>(PDF / JPG / PNG)</span>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                component="label"
                startIcon={<UploadFileIcon sx={{ fontSize: 18 }} />}
                sx={{
                  height: "38px", px: 2, textTransform: "none", fontSize: "13px", fontWeight: 600,
                  color: "#2B6CB0", border: "1.5px solid #2B6CB0", borderRadius: "8px", background: "#fff",
                  "&:hover": { background: "#E6F1FB" },
                }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={handlePoFile}
                />
              </Button>
              <Typography sx={{ fontSize: "12px", color: "#4A5568", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {poFile ? poFile.name : "No file chosen"}
              </Typography>
              {poFile && (
                <IconButton size="small" onClick={() => setPoFile(null)}>
                  <CloseIcon sx={{ fontSize: 16, color: "#E53E3E" }} />
                </IconButton>
              )}
            </Box>
          </Box>
        )}
      </Grid>

    </Grid>
  </Box>
</Box>

          {/* ── Section 2: Product Details Table ── */}
          <Box sx={formCardSx}>
            {/* <Box sx={sectionHeaderSx}>
              <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />
              <Typography sx={sectionHeaderTitleSx}>Product Details</Typography>
            </Box> */}

            <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: "none" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <StyledTableRow>
                    {["Sr No", "Product Code", "Product Description", "Unit Rate", "Transport Cost", "Quantity", "UOM", "PO Validity Date", "Basic Amount", "GST %", "Total Amount", "Action"].map((head, i) => (
                      <StyledTableCell key={i} align="left" sx={tableHeadCellSx}>{head}</StyledTableCell>
                    ))}
                  </StyledTableRow>
                </TableHead>
                <TableBody sx={{ background: "#fff" }}>
                  {child_details.map((row, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell align="center" sx={{ fontSize: "12px" }}>{index + 1}</StyledTableCell>

                      {/* Product Code */}
                      <StyledTableCell>
                        <TextField select fullWidth size="small" variant="standard"
                          name="prod_code" value={row?.prod_code || ""}
                          onChange={e => handleChangeInput(e, index)}
                          sx={tableSelectSx}
                          InputProps={{ disableUnderline: true }}
                        >
                          {Product.map(item => (
                            <MenuItem key={item.prod_code} value={item.prod_code} sx={{ fontSize: "12px" }}>
                              {item.prod_code}
                            </MenuItem>
                          ))}
                        </TextField>
                      </StyledTableCell>

                      {/* Product Description */}
                      <StyledTableCell>
                        <TextField fullWidth size="small" variant="standard"
                          name="prod_description" value={row?.prod_description || ""}
                          sx={tableCellInputSx}
                          InputProps={{ readOnly: true, disableUnderline: true }}
                        />
                      </StyledTableCell>

                      {/* Unit Rate */}
                      <StyledTableCell>
                        <TextField fullWidth size="small" variant="standard"
                          name="unit_rate" value={row?.unit_rate || ""}
                          onChange={e => handleChangeInput(e, index)}
                          sx={tableCellInputSx} InputProps={{ disableUnderline: true }}
                        />
                      </StyledTableCell>

                      {/* Transport Cost */}
                      <StyledTableCell>
                        <TextField fullWidth size="small" variant="standard"
                          name="transport_cost" value={row?.transport_cost || ""}
                          onChange={e => handleChangeInput(e, index)}
                          sx={tableCellInputSx} InputProps={{ disableUnderline: true }}
                        />
                      </StyledTableCell>

                      {/* Quantity */}
                      <StyledTableCell>
                        <TextField fullWidth size="small" variant="standard"
                          name="qty_kg" value={row?.qty_kg || ""}
                          onChange={e => handleChangeInput(e, index)}
                          sx={tableCellInputSx} InputProps={{ disableUnderline: true }}
                        />
                      </StyledTableCell>

                      {/* UOM */}
                      <StyledTableCell>
                        <TextField select fullWidth size="small" variant="standard"
                          name="uom" value={row?.uom || "m3"}
                          onChange={e => handleChangeInput(e, index)}
                          sx={tableSelectSx} InputProps={{ disableUnderline: true }}
                        >
                          {uom.filter(item => item.material_type === "fg").map(item => (
                            <MenuItem key={item.uom_id} value={item.uom} sx={{ fontSize: "12px" }}>
                              {item.uom}
                            </MenuItem>
                          ))}
                        </TextField>
                      </StyledTableCell>

                      {/* PO Validity Date */}
                      <StyledTableCell>
                        <DatePicker format="DD/MM/YYYY"
                          value={row.req_date ? dayjs(row.req_date) : null}
                          onChange={v => handleChangeInput({ target: { name: "req_date", value: v ? v.format("YYYY-MM-DD") : "" } }, index)}
                          sx={{ width: "140px", "& .MuiOutlinedInput-root": { height: "30px", fontSize: "12px", "& fieldset": { border: "none" } } }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </StyledTableCell>

                      {/* Basic Amount */}
                      <StyledTableCell>
                        <TextField fullWidth size="small" variant="standard"
                          name="basic_amount" value={row?.basic_amount || ""}
                          disabled sx={tableCellInputSx}
                          InputProps={{ disableUnderline: true }}
                        />
                      </StyledTableCell>

                      {/* GST % */}
                      <StyledTableCell>
                        <TextField fullWidth size="small" variant="standard" type="number"
                          name="gst_percentage" value={row?.gst_percentage || ""}
                          onChange={e => handleChangeInput(e, index)}
                          sx={tableCellInputSx} InputProps={{ disableUnderline: true }}
                        />
                      </StyledTableCell>

                      {/* Total Amount */}
                      <StyledTableCell>
                        <TextField fullWidth size="small" variant="standard"
                          name="total_amount" value={row?.total_amount || ""}
                          disabled sx={tableCellInputSx}
                          InputProps={{ disableUnderline: true }}
                        />
                      </StyledTableCell>

                      {/* Action */}
                      <StyledTableCell align="center">
                        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                          <IconButton size="small" onClick={handleAddScheduleFields}>
                            <AddIcon sx={{ fontSize: 16, color: "#2B6CB0", border: "1px solid #2B6CB0", borderRadius: "50%" }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRemoveScheduledetails(index)} disabled={index === 0}>
                            <DeleteForeverIcon sx={{ fontSize: 18, color: index === 0 ? "#c0c0c0" : "#E53E3E" }} />
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer */}
            <Box sx={formFooterSx}>
              <Button sx={cancelBtnSx} onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" sx={saveBtnSx}>
                <SaveIcon sx={{ fontSize: 18 }} />
                {editData ? "Update" : "Save"}
              </Button>
            </Box>
          </Box>
        </form>

        {/* Snackbar */}
        <ReusableSnackbar
          open={!!successMessage}
          onClose={(_, reason) => { if (reason !== "clickaway") setSuccessMessage(""); }}
          message={successMessage}
          autoHideDuration={2000}
          vertical="top" horizontal="right"
        />
      </Box>
    </LocalizationProvider>
  );
};

export default ScheduleEntryAdd;